package com.trevorwiebe.timesheet.calendar.data

import com.trevorwiebe.timesheet.calendar.domain.CalendarRepository
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.dto.TimeOffRequestDto
import com.trevorwiebe.timesheet.core.domain.dto.TimeOffReviewDto
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map

class CalendarRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository,
) : CalendarRepository {

    override suspend fun postTimeOffList(timeOffList: List<TimeOffRequestDto>): TSResult {
        return errorWrapper{
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            val userIdResult = coreRepository.getUserId()
            if (userIdResult.error != null) userIdResult
            val userId = userIdResult.data as String

            val timeOffRequestsCollection = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(userId)
                .collection("timeOffRequests")

            timeOffList.forEach {
                timeOffRequestsCollection.add(it)
            }

            TSResult(data = "success")
        }
    }

    override suspend fun getTimeOffList(): Flow<TSResult> {

        val flow = try {
            val organizationIdResult = coreRepository.getOrganizationId()
            val organizationId = organizationIdResult.data as? String
                ?: throw IllegalStateException(organizationIdResult.error ?: "Missing organization")
            val organizationPath = "organizations/$organizationId/"

            firebaseDatabase.firestore
                .collectionGroup("timeOffRequests")
                .snapshots
                .map { snapshot ->
                    val requests = snapshot.documents
                        .filter { it.reference.path.startsWith(organizationPath) }
                        .mapNotNull { document ->
                            try {
                                document.data<TimeOffRequestDto>().toTimeOffRequestModel(document.id)
                            } catch (e: Exception) {
                                null
                            }
                        }
                    TSResult(data = requests)
                }
                .catch { e ->
                    emit(TSResult(error = e.message ?: "Unknown error fetching time off requests"))
                }
        } catch (e: Exception) {
            flow { emit(TSResult(error = e.message ?: "Unknown error")) }
        }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun deleteTimeOffRequest(employeeId: String, requestId: String): TSResult {
        return errorWrapper {
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(employeeId)
                .collection("timeOffRequests")
                .document(requestId)
                .delete()

            TSResult(data = "Success")
        }
    }

    override suspend fun reviewTimeOffRequest(
        employeeId: String,
        requestId: String,
        approved: Boolean,
    ): TSResult {
        return errorWrapper {
            val organizationIdResult = coreRepository.getOrganizationId()
            if (organizationIdResult.error != null) organizationIdResult
            val organizationId = organizationIdResult.data as String

            val reviewDto = TimeOffReviewDto(
                approved = approved,
                reviewedDate = Util.localDateTime(atTopOfDay = true).date,
            )

            firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(employeeId)
                .collection("timeOffRequests")
                .document(requestId)
                .set(reviewDto, merge = true)

            TSResult(data = "Success")
        }
    }

}